import { Wallet, TrendingUp, AlertTriangle } from 'lucide-react';

const inr = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const pct = (n, d) => (d > 0 ? Math.max(0, Math.min(100, (n / d) * 100)) : 0);

/** Revenue vs Outstanding — stacked horizontal bar split into
 *  Collected · Outstanding · Overdue. Includes explicit legend + values. */
export function RevenueOutstandingBar({ totals }) {
  const total = Number(totals?.total_value || 0);
  const paid = Number(totals?.total_paid || 0);
  const overdue = Number(totals?.overdue_amount || 0);
  const unpaidRaw = Number(totals?.total_unpaid || 0);
  // The unpaid figure already includes anything past-due, so split it visually.
  const unpaidNotOverdue = Math.max(0, unpaidRaw - overdue);

  const paidPct = pct(paid, total);
  const unpaidPct = pct(unpaidNotOverdue, total);
  const overduePct = pct(overdue, total);

  const collectionRate = total > 0 ? (paid / total) * 100 : 0;

  return (
    <div className="card p-4 elev-1" data-testid="fin-chart-revenue-outstanding">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Wallet size={14} className="text-[hsl(var(--primary))]" />
          <span className="text-[13px] font-semibold">Revenue vs Outstanding</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--foreground-muted))]"
             data-testid="fin-chart-collection-rate">
          <TrendingUp size={11} className="text-[hsl(var(--success))]" />
          Collection rate <span className="font-semibold tabular-nums text-[hsl(var(--foreground))]">{collectionRate.toFixed(1)}%</span>
        </div>
      </div>

      {total <= 0 ? (
        <EmptyChart label="Add project values to see your revenue split." />
      ) : (
        <>
          <div className="h-6 w-full rounded-lg overflow-hidden flex bg-[hsl(var(--surface-2))]"
               role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={paidPct.toFixed(0)}
               data-testid="fin-chart-bar">
            {paidPct > 0 && (
              <div className="h-full" style={{ width: `${paidPct}%`, background: 'hsl(var(--success))' }}
                   title={`Collected ${inr(paid)}`} data-testid="fin-chart-seg-paid" />
            )}
            {unpaidPct > 0 && (
              <div className="h-full" style={{ width: `${unpaidPct}%`, background: 'hsl(var(--warning))' }}
                   title={`Outstanding ${inr(unpaidNotOverdue)}`} data-testid="fin-chart-seg-unpaid" />
            )}
            {overduePct > 0 && (
              <div className="h-full" style={{ width: `${overduePct}%`, background: 'hsl(var(--danger))' }}
                   title={`Overdue ${inr(overdue)}`} data-testid="fin-chart-seg-overdue" />
            )}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-[12px]">
            <LegendCell dot="hsl(var(--success))" label="Collected"   value={inr(paid)}            sub={`${paidPct.toFixed(1)}%`} tone="success" />
            <LegendCell dot="hsl(var(--warning))" label="Outstanding" value={inr(unpaidNotOverdue)} sub={`${unpaidPct.toFixed(1)}%`} tone="warning" />
            <LegendCell dot="hsl(var(--danger))"  label="Overdue"     value={inr(overdue)}         sub={`${overduePct.toFixed(1)}%`} tone="danger" />
          </div>
        </>
      )}
    </div>
  );
}

/** Per-project payment progress bars. One row per project with a filled
 *  "paid" segment on top of an "unpaid" track, plus an overdue marker on
 *  the right when a project has overdue milestones. */
export function PerProjectPaymentBars({ rows, limit = 12 }) {
  const withValue = (rows || []).filter(r => Number(r.total_amount || 0) > 0);
  const sorted = [...withValue].sort((a, b) => Number(b.total_amount || 0) - Number(a.total_amount || 0));
  const shown = sorted.slice(0, limit);
  const hiddenCount = Math.max(0, sorted.length - shown.length);

  return (
    <div className="card p-4 elev-1" data-testid="fin-chart-per-project">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={14} className="text-[hsl(var(--primary))]" />
        <span className="text-[13px] font-semibold">Per-project payment progress</span>
        <span className="ml-auto text-[11px] text-[hsl(var(--foreground-muted))]">
          {shown.length} of {sorted.length} projects
        </span>
      </div>

      {shown.length === 0 ? (
        <EmptyChart label="No project has an amount yet — enter Total on Master Project Data to see progress bars." />
      ) : (
        <div className="space-y-2.5" data-testid="fin-chart-project-rows">
          {shown.map(r => {
            const total = Number(r.total_amount || 0);
            const paid = Number(r.paid_amount || 0);
            const overdue = Number(r.overdue_amount || 0);
            const paidPct = pct(paid, total);
            const overduePct = pct(overdue, total);
            return (
              <div key={r.id} data-testid={`fin-chart-project-${r.id}`}>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-[12.5px] font-medium truncate flex-1" title={r.project_name}>{r.project_name}</span>
                  <span className="text-[11px] text-[hsl(var(--foreground-muted))] truncate max-w-[160px]" title={r.client}>{r.client}</span>
                  <span className="text-[12px] tabular-nums font-semibold ml-1">{paidPct.toFixed(0)}%</span>
                </div>
                <div className="relative h-2.5 w-full rounded-full overflow-hidden bg-[hsl(var(--surface-2))]"
                     role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={paidPct.toFixed(0)}>
                  <div className="absolute inset-y-0 left-0" style={{ width: `${paidPct}%`, background: 'hsl(var(--success))' }} />
                  {overduePct > 0 && (
                    <div className="absolute inset-y-0 right-0 opacity-90"
                         style={{ width: `${overduePct}%`, background: 'hsl(var(--danger))' }}
                         title={`Overdue ${inr(overdue)}`} />
                  )}
                </div>
                <div className="mt-1 flex items-center gap-3 text-[11px] text-[hsl(var(--foreground-muted))]">
                  <span className="tabular-nums">{inr(paid)} <span className="opacity-60">/ {inr(total)}</span></span>
                  {overdue > 0 && (
                    <span className="inline-flex items-center gap-1 text-[hsl(var(--danger))]">
                      <AlertTriangle size={10} />Overdue {inr(overdue)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {hiddenCount > 0 && (
            <div className="text-[11px] text-[hsl(var(--foreground-subtle))] pt-1">
              +{hiddenCount} more project{hiddenCount === 1 ? '' : 's'} — see the table below.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LegendCell({ dot, label, value, sub }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-1.5 inline-block w-2 h-2 rounded-full" style={{ background: dot }} />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">{label}</div>
        <div className="text-[14px] font-semibold tabular-nums truncate">{value}</div>
        <div className="text-[11px] text-[hsl(var(--foreground-muted))] tabular-nums">{sub}</div>
      </div>
    </div>
  );
}

function EmptyChart({ label }) {
  return (
    <div className="py-6 text-center text-[12.5px] text-[hsl(var(--foreground-muted))]">
      {label}
    </div>
  );
}
